import { useEffect } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { LIVING_SITUATIONS, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { UseFormSetValue } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';

import AddressFieldsLayout from './AddressElements/AddressFieldsLayout.tsx';
import Land from './AddressElements/Land.tsx';
import NumberOfPeopleInHouse from './AddressElements/NumberOfPeopleInHouse.tsx';
import { SharedHouseholdSelect } from './SharedHouseholdSelect.tsx';
import Wohnart from './Wohnart.tsx';
import WohnhaftSeit from './WohnhaftSeit.tsx';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaLivingSituationForm } from '~/features/leadDetails/schemas/living.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    antragsteller: 1 | 2;
};

// NOTE: all setValue calls must set shouldDirty, otherwise the LeadDetailsFormContext
// considers the form untouched and resets it back to the lead data on the next refetch
const clearAddress = ({
    applicant,
    setValue,
}: {
    applicant: 1 | 2;
    setValue: UseFormSetValue<SchemaLivingSituationForm>;
}) => {
    setValue(`antragsteller${applicant}.wohnsituation.anschrift.strasse`, '', { shouldDirty: true });
    setValue(`antragsteller${applicant}.wohnsituation.anschrift.hausnummer`, '', { shouldDirty: true });
    setValue(`antragsteller${applicant}.wohnsituation.anschrift.plz`, '', { shouldDirty: true });
    setValue(`antragsteller${applicant}.wohnsituation.anschrift.ort`, '', { shouldDirty: true });
    setValue(`antragsteller${applicant}.wohnsituation.anschrift.land`, null, { shouldDirty: true });
    setValue(`antragsteller${applicant}.wohnsituation.anzahlPersonenImHaushalt`, null, { shouldDirty: true });
    setValue(`antragsteller${applicant}.wohnsituation.wohnart`, null, { shouldDirty: true });
};

const Address: React.FC<Props> = ({ antragsteller }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { setIsLivingInOwnHouseApplicant1, setIsLivingInOwnHouseApplicant2, setIsLivingInSharedHousehold } =
        useLeadDetailsFormContext();

    const gemeinsamerHaushalt1 = useWatch<SchemaLivingSituationForm>({
        name: 'antragsteller1.wohnsituation.gemeinsamerHaushalt',
    }) as SchemaLivingSituationForm['antragsteller1']['wohnsituation']['gemeinsamerHaushalt'];

    const gemeinsamerHaushalt2 = useWatch<SchemaLivingSituationForm>({
        name: 'antragsteller2.wohnsituation.gemeinsamerHaushalt',
    }) as boolean | null | undefined;

    const { getValues, setValue } = useFormContext<SchemaLivingSituationForm>();

    const hasSecondApplicant = gemeinsamerHaushalt2 !== undefined;
    const isSharedHousehold = gemeinsamerHaushalt1 === true || gemeinsamerHaushalt2 === true;
    // a shared household has a single address, which is held by the applicant flagged as sharing it
    const sharedHouseholdApplicant = gemeinsamerHaushalt1 === true ? 1 : 2;
    // the household is switched on the card holding the address, which is applicant 1 unless already shared by applicant 2
    const showHouseholdSelect = hasSecondApplicant && (antragsteller === 1 || isSharedHousehold);

    const onChangeSharedHousehold = (sharedHousehold: boolean) => {
        if (sharedHousehold === isSharedHousehold) return;
        setIsLivingInSharedHousehold(sharedHousehold);
        setValue('antragsteller1.wohnsituation.gemeinsamerHaushalt', sharedHousehold, { shouldDirty: true });
        setValue('antragsteller2.wohnsituation.gemeinsamerHaushalt', false, { shouldDirty: true });
        if (!sharedHousehold) return;
        // a shared household is held by applicant 1, so the address of applicant 2 is dropped instead of merged
        clearAddress({ applicant: 2, setValue });
        setIsLivingInOwnHouseApplicant1(
            getValues('antragsteller1.wohnsituation.wohnart') === LIVING_SITUATIONS.IM_EIGENEN_HAUS,
        );
        setIsLivingInOwnHouseApplicant2(false);
    };

    useEffect(() => {
        if (isSharedHousehold) setIsLivingInSharedHousehold(true);
    }, [isSharedHousehold, setIsLivingInSharedHousehold]);

    if (isSharedHousehold && antragsteller !== sharedHouseholdApplicant) return null;

    return (
        <FormCard>
            {showHouseholdSelect && (
                <SharedHouseholdSelect
                    applicant={antragsteller}
                    sharedHousehold={isSharedHousehold}
                    onChange={onChangeSharedHousehold}
                    disabled={
                        !canExecuteApplicantCommand({
                            applicant: 1,
                            commandName: LOAN_REQUEST_COMMANDS.changeApplicantLivingSituation,
                            permissions,
                        }) ||
                        !canExecuteApplicantCommand({
                            applicant: 2,
                            commandName: LOAN_REQUEST_COMMANDS.changeApplicantLivingSituation,
                            permissions,
                        })
                    }
                />
            )}
            <AddressFieldsLayout addressKind="anschrift" antragsteller={antragsteller}>
                <Land addressKind="anschrift" antragsteller={antragsteller} />
                <WohnhaftSeit antragsteller={antragsteller} addressKind="anschrift" />
                <Wohnart antragsteller={antragsteller} />
                <NumberOfPeopleInHouse antragsteller={antragsteller} />
            </AddressFieldsLayout>
        </FormCard>
    );
};

export default Address;
